// Sistema de feedback/rating + telemetria de navegação (client-side).
//
//  - Botão manual na toolbar abre um diálogo de avaliação (5 estrelas +
//    comentário) sobre a TELA ATUAL, gravado no backend (MILLENIUM.FEEDBACK.Insert).
//  - Prompt periódico (a cada N dias por usuário) abre o mesmo diálogo sozinho.
//  - Telemetria de navegação: acumula {tela, título, permanência} localmente e
//    envia em LOTE após timeout / na saída (MILLENIUM.FEEDBACK.LogNavigation).
//    Best-effort: o buffer é persistido em localStorage, então sobrevive a reload
//    e é reenviado depois; se perder, tudo bem (não é dado crítico).

import { CommandDialogWindow } from "./wts.ui.overlays"
import { Client } from "./wts.client"
import "./wts.ui.feedback.scss"

interface FrameLike {
    key: string
    caption?: string
    route?: { urlKey?: string, key?: string, target?: string } | undefined
    moduleName?: string
}

interface NavEvent { SCREEN: string, SCREEN_TITLE: string, DWELL_SECONDS: number }

const PROMPT_KEY = "@mln:fb:lastprompt:"      // + userKey
const BUFFER_KEY = "@mln:fb:navbuffer"
const PROMPT_INTERVAL_DAYS = 14
const FLUSH_INTERVAL_MS = 120_000              // 2 min
const MAX_BUFFER = 200                         // teto do buffer (descarta o excedente)
const NAV_BEFORE_PROMPT = 3                    // só oferece o prompt após N navegações

function normScreen(raw?: string): string {
    let s = (raw || "").trim()
    if (!s) return ""
    const comma = s.lastIndexOf(",")
    if (comma >= 0) s = s.slice(comma + 1)
    if (s.charAt(0) === "#") s = s.slice(1)
    const amp = s.indexOf("&")
    if (amp >= 0) s = s.slice(0, amp)
    return s.toLowerCase()
}

function screenIdOf(frame: FrameLike): string {
    const r = frame.route
    return normScreen(frame.key) || normScreen(r?.urlKey) || normScreen(r?.key)
        || normScreen(r?.target) || normScreen(frame.moduleName) || ""
}

class FeedbackEngine {
    private userKey = ""
    private wsid = ""
    private version = ""
    private sessionId = ""
    private buffer: NavEvent[] = []
    private current?: { tela: string, titulo: string, enter: number }
    private flushTimer?: any
    private navCount = 0
    private promptedThisSession = false
    private dialogOpen = false
    private started = false

    /** Ligado/desligado (ex.: desligar em contingência). */
    enabled = true

    /** Log de diagnóstico. Ative com localStorage['@mln:fb:debug']='1' ou Feedback.debug=true. */
    debug = (() => { try { return localStorage.getItem("@mln:fb:debug") === "1" } catch { return false } })()
    private dbg(...a: any[]) { if (this.debug) console.log("%c[fb]", "color:#e08a3a", ...a) }

    // ── Ciclo de vida ──

    init() {
        if (this.started) return
        this.started = true
        // Recupera buffer persistido de sessões anteriores (durabilidade).
        try {
            const raw = localStorage.getItem(BUFFER_KEY)
            if (raw) this.buffer = JSON.parse(raw) || []
        } catch { this.buffer = [] }

        this.flushTimer = setInterval(() => void this.flush(), FLUSH_INTERVAL_MS)
        window.addEventListener("pagehide", this.handleUnload)
        document.addEventListener("visibilitychange", this.handleVisibility)
    }

    setUser(userKey: string, wsid: string, version = "") {
        this.userKey = userKey
        this.wsid = wsid
        if (version) this.version = version
        if (!this.sessionId) this.sessionId = this.newSessionId()
        // Puxa a versão do app se não veio (não bloqueia).
        if (!this.version) void this.loadVersion()
        // Reenvia buffer pendente logo após login.
        void this.flush()
    }

    private newSessionId(): string {
        return Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 8)
    }

    private async loadVersion() {
        try {
            const v = await millennium.browser.getVersion()
            this.version = v?.["millennium"] || v?.["omni"] || ""
        } catch { /* sem versão — segue */ }
    }

    private handleUnload = () => { this.closeCurrent(); void this.flush() }
    private handleVisibility = () => { if (document.visibilityState === "hidden") { this.closeCurrent(); void this.flush() } }

    // ── Telemetria de navegação ──

    /** Chamado pelo core na ativação de cada tela. */
    trackScreen(frame: FrameLike) {
        if (!this.enabled) return
        const tela = screenIdOf(frame)
        if (!tela) return
        this.closeCurrent()
        this.current = { tela, titulo: frame.caption || "", enter: Date.now() }
        this.navCount++
        this.maybePrompt()
    }

    /** Fecha a tela atual, registrando a permanência como evento. */
    private closeCurrent() {
        if (!this.current) return
        const dwell = Math.max(0, Math.round((Date.now() - this.current.enter) / 1000))
        this.pushEvent({ SCREEN: this.current.tela, SCREEN_TITLE: this.current.titulo, DWELL_SECONDS: dwell })
        this.current = undefined
    }

    private pushEvent(e: NavEvent) {
        this.buffer.push(e)
        if (this.buffer.length > MAX_BUFFER) this.buffer = this.buffer.slice(-MAX_BUFFER)
        this.persistBuffer()
        if (this.buffer.length >= 50) void this.flush()
    }

    private persistBuffer() {
        try { localStorage.setItem(BUFFER_KEY, JSON.stringify(this.buffer)) } catch { /* quota */ }
    }

    /** Envia o lote acumulado. Best-effort: em falha, mantém o buffer p/ retry. */
    async flush() {
        if (!this.enabled || !this.buffer.length) return
        const batch = this.buffer
        this.buffer = []
        this.persistBuffer()
        try {
            await Client.get("MILLENIUM.FEEDBACK.LogNavigation", {
                WSID: this.wsid, SESSION_ID: this.sessionId, EVENTS: batch,
            })
        } catch {
            // Devolve ao buffer (limitado) para tentar de novo depois. Perder é aceitável.
            this.buffer = batch.concat(this.buffer).slice(-MAX_BUFFER)
            this.persistBuffer()
        }
    }

    // ── Prompt periódico ──

    private maybePrompt() {
        if (this.promptedThisSession || this.dialogOpen || !this.enabled) return
        if (this.navCount < NAV_BEFORE_PROMPT) { this.dbg(`prompt: navegações ${this.navCount}/${NAV_BEFORE_PROMPT}`); return }
        const last = Number(localStorage.getItem(PROMPT_KEY + this.userKey) || 0)
        if (last && (Date.now() - last) < PROMPT_INTERVAL_DAYS * 86_400_000) {
            this.dbg(`prompt: última avaliação há ~${Math.round((Date.now() - last) / 86_400_000)}d (aguarda ${PROMPT_INTERVAL_DAYS}d)`); return
        }
        this.promptedThisSession = true
        this.dbg("prompt: disparando avaliação automática em 2.5s")
        // Pequeno atraso para não abrir em cima de uma troca de tela.
        setTimeout(() => { if (this.current) void this.showDialog(this.current.tela, this.current.titulo, true) }, 2500)
    }

    private markPrompted() {
        try { localStorage.setItem(PROMPT_KEY + this.userKey, String(Date.now())) } catch { }
    }

    // ── Diálogo de avaliação ──

    /** Abre o diálogo para a tela do frame informado (uso: botão da toolbar). */
    promptForFrame(frame?: FrameLike) {
        const tela = frame ? screenIdOf(frame) : (this.current?.tela ?? "")
        const titulo = frame?.caption ?? this.current?.titulo ?? ""
        void this.showDialog(tela, titulo, false)
    }

    private async showDialog(tela: string, titulo: string, periodic: boolean) {
        if (this.dialogOpen) return
        this.dialogOpen = true
        // Periódico: já reprograma o próximo automático (o usuário foi perguntado).
        // Manual: só reprograma se o usuário ENVIAR (ver submit) — cancelar o botão
        // não deve suprimir a avaliação automática.
        if (periodic) this.markPrompted()

        const content = document.createElement("div")
        content.className = "mln-fb-dialog"

        const question = document.createElement("div")
        question.className = "mln-fb-question"
        question.textContent = periodic
            ? "Como está sua experiência com o sistema?"
            : "Como está sua experiência nesta tela?"
        content.appendChild(question)

        if (titulo) {
            const ctx = document.createElement("div")
            ctx.className = "mln-fb-context"
            ctx.textContent = titulo
            content.appendChild(ctx)
        }

        const starsBox = document.createElement("div")
        starsBox.className = "mln-fb-stars"
        let nota = 0
        const stars: HTMLButtonElement[] = []
        const paint = (v: number) => stars.forEach((s, i) => s.classList.toggle("active", i < v))
        for (let n = 1; n <= 5; n++) {
            const star = document.createElement("button")
            star.className = "mln-fb-star"
            star.type = "button"
            star.setAttribute("aria-label", `${n} estrela${n > 1 ? "s" : ""}`)
            star.innerHTML = `<i class="fa fa-star"></i>`
            star.onmouseenter = () => paint(n)
            star.onclick = () => { nota = n; paint(n) }
            stars.push(star)
            starsBox.appendChild(star)
        }
        starsBox.onmouseleave = () => paint(nota)
        content.appendChild(starsBox)

        const comment = document.createElement("textarea")
        comment.className = "mln-fb-comment"
        comment.placeholder = "Conte o que achou (opcional)"
        comment.rows = 3
        content.appendChild(comment)

        const dlg = new CommandDialogWindow()
        try {
            await dlg.show(content, {
                title: "Avaliação",
                className: "mln-fb-dialogw",
                width: "380px",
                lightDismiss: true,
                commands: [
                    { id: "cancel", caption: periodic ? "Agora não" : "Cancelar" },
                    {
                        id: "send", caption: "Enviar", primary: true, callback: async () => {
                            if (nota <= 0) { millennium.browser.notify("Escolha uma nota de 1 a 5.", "warning"); return }
                            const ok = await this.submit(tela, titulo, nota, comment.value.trim())
                            if (ok) dlg.close("send")
                        },
                    },
                ],
            })
        } finally {
            this.dialogOpen = false
        }
    }

    private async submit(tela: string, titulo: string, nota: number, comentario: string): Promise<boolean> {
        try {
            await Client.get("MILLENIUM.FEEDBACK.Insert", {
                SCREEN: tela, SCREEN_TITLE: titulo, RATING: nota,
                COMMENTS: comentario, WSID: this.wsid, APP_VERSION: this.version,
            })
            millennium.browser.notify("Obrigado pelo seu feedback!", "success")
            this.markPrompted() // avaliou → reprograma o próximo automático (14 dias)
            return true
        } catch {
            millennium.browser.notify("Não foi possível enviar agora. Tente novamente mais tarde.", "warning")
            return false
        }
    }
}

const GLOBAL_KEY = "___MLN_FEEDBACK"
if (!(window as any)[GLOBAL_KEY])
    (window as any)[GLOBAL_KEY] = new FeedbackEngine()

export const Feedback: FeedbackEngine = (window as any)[GLOBAL_KEY]
export type { FeedbackEngine }
